import { objectModule } from "@/modules/object";
import { logger } from "@/modules/logger";
/* eslint-disable @typescript-eslint/no-explicit-any */

export type DesktopRealtimeEventType =
  | "transcript.data"
  | "transcript.partial_data"
  | "audio_mixed_raw.data";

export type ParsedTranscriptWord = {
  text: string;
  start: number; // ms
  end: number; // ms
  confidence: number;
};

export type ParsedTranscriptEvent = {
  type: Extract<DesktopRealtimeEventType, "transcript.data" | "transcript.partial_data">;
  transcript: string;
  endOfTurn: boolean;
  timestamp: number; // ms
  words: ParsedTranscriptWord[];
  speakerIsHost?: boolean;
  participantId?: number | string;
  recordingId?: string;
  transcriptId?: string;
  windowId?: string;
};

export type ParsedAudioEvent = {
  type: Extract<DesktopRealtimeEventType, "audio_mixed_raw.data">;
  bufferBase64: string;
  timestamp: number; // ms
  windowId?: string;
};

export type ParsedDesktopEvent = ParsedTranscriptEvent | ParsedAudioEvent | null;

export function parseDesktopRealtimeEnvelope(input: {
  event: unknown;
  kind?: string;
}): ParsedDesktopEvent {
  try {
    const maybeEnvelope = input as { event?: unknown; kind?: string } | undefined;
    const raw = maybeEnvelope?.event;

    let inner: any = raw;
    if (typeof raw === "string") {
      inner = JSON.parse(raw);
    }

    if (inner && typeof inner === "object" && "payload" in inner && inner.payload) {
      inner = inner.payload;
    }

    const kind: string | undefined = inner?.event;
    const payloadContainer = inner?.data || {};
    const payloadData = payloadContainer?.data || payloadContainer || {};
    const windowId: string | undefined =
      typeof inner?.window?.id === "string" ? (inner.window.id as string) : undefined;

    if (kind === "audio_mixed_raw.data") {
      const base64 = payloadData?.buffer;
      const tsRelSec: number | undefined = payloadData?.timestamp?.relative;
      if (typeof base64 !== "string") return null;
      const timestamp = Math.round((tsRelSec ?? 0) * 1000);
      return { type: "audio_mixed_raw.data", bufferBase64: base64, timestamp, windowId };
    }

    if (kind === "transcript.data" || kind === "transcript.partial_data") {
      const wordsRaw = extractWords(payloadData);
      const transcript: string = wordsRaw
        .map((w: any) => w?.text || "")
        .join(" ")
        .trim();
      if (!transcript) return null;

      const parsedWords: ParsedTranscriptWord[] = wordsRaw.map((w: any) => ({
        text: typeof w?.text === "string" ? w.text : "",
        start: parseRelativeTimestampMs(w?.start_timestamp),
        end: parseRelativeTimestampMs(w?.end_timestamp),
        confidence: 0,
      }));

      const participantIsHost = Boolean(payloadData?.participant?.is_host);
      const participantId = extractIdentifier(payloadData?.participant?.id);
      const recordingId = extractStringIdentifier(payloadContainer?.recording?.id);
      const transcriptId = extractStringIdentifier(payloadContainer?.transcript?.id);
      const timestamp = resolveEventTimestamp(wordsRaw);

      return {
        type: kind,
        transcript,
        endOfTurn: kind === "transcript.data",
        timestamp,
        words: parsedWords,
        speakerIsHost: participantIsHost,
        participantId,
        recordingId,
        transcriptId,
        windowId,
      };
    }

    return null;
  } catch (err) {
    logger.warn({
      message: "[desktop-event-parser] failed to parse envelope",
      info: { error: objectModule.safeErrorAsJson(err as Error) },
    });
    return null;
  }
}

function extractIdentifier(value: unknown): number | string | undefined {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string" && value.length > 0) {
    return value;
  }

  return undefined;
}

function extractStringIdentifier(value: unknown): string | undefined {
  if (typeof value === "string" && value.length > 0) {
    return value;
  }

  return undefined;
}

function extractWords(payloadData: unknown): any[] {
  if (!payloadData || typeof payloadData !== "object") return [];

  if (Array.isArray((payloadData as { words?: unknown }).words)) {
    return (payloadData as { words: unknown[] }).words;
  }

  const nested = (payloadData as { data?: unknown }).data;
  if (
    nested &&
    typeof nested === "object" &&
    Array.isArray((nested as { words?: unknown }).words)
  ) {
    return (nested as { words: unknown[] }).words;
  }

  return [];
}

function parseRelativeTimestampMs(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) {
    return Math.round(value * 1000);
  }

  if (value && typeof value === "object") {
    const maybeRelative = (value as { relative?: unknown }).relative;
    if (typeof maybeRelative === "number" && Number.isFinite(maybeRelative)) {
      return Math.round(maybeRelative * 1000);
    }

    const maybeAbsolute = (value as { absolute?: unknown }).absolute;
    if (typeof maybeAbsolute === "string") {
      const parsed = Date.parse(maybeAbsolute);
      if (!Number.isNaN(parsed)) return parsed;
    }
  }

  return 0;
}

function resolveEventTimestamp(words: any[]): number {
  const last = words[words.length - 1];
  if (last) {
    const endMs = parseRelativeTimestampMs(last?.end_timestamp);
    if (endMs) return endMs;
    const startMs = parseRelativeTimestampMs(last?.start_timestamp);
    if (startMs) return startMs;
  }
  return Date.now();
}
